import { useEffect, useState, useRef } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { FolderOpen, Upload, Download, Trash2, FileText, FileSpreadsheet, File, Loader2, Table2, Plus, Save, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATS = ["Company Docs", "Statements", "Bank Statements", "Reports", "Other"];

const iconFor = (name) => {
  const e = (name.split(".").pop() || "").toLowerCase();
  if (["xlsx", "xls", "csv"].includes(e)) return FileSpreadsheet;
  if (["pdf", "doc", "docx", "txt"].includes(e)) return FileText;
  return File;
};
const kb = (n) => (!n ? "" : n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function Documents() {
  const { companyId } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("All");
  const [uploadCat, setUploadCat] = useState("Company Docs");
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    api.get(`/companies/${companyId}/documents`).then(({ data }) => setItems(data))
      .catch(() => toast.error("Failed to load documents")).finally(() => setLoading(false));
  };
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("category", uploadCat);
    setUploading(true);
    try {
      await api.post(`/companies/${companyId}/documents`, fd);
      toast.success("Uploaded");
      load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/companies/${companyId}/documents/${id}`);
    toast.success("Deleted"); load();
  };

  const filtered = tab === "All" ? items : items.filter((x) => x.category === tab);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="documents-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900">Documents</h1>
          <p className="text-slate-500 text-sm mt-0.5">Store company docs, statements & sheets in one place — upload PDFs, Excel or CSV and edit spreadsheets right here.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={uploadCat} onChange={(e) => setUploadCat(e.target.value)} data-testid="upload-category"
            className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white outline-none">
            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input ref={fileRef} type="file" className="hidden" onChange={onUpload} data-testid="doc-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-doc-btn"
            className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload
          </button>
        </div>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {["All", ...CATS].map((c) => (
          <button key={c} onClick={() => setTab(c)} data-testid={`doc-tab-${c.replace(/\s/g, "-")}`}
            className={`px-3.5 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === c ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-indigo-600" size={28} /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-400 flex flex-col items-center gap-3">
          <FolderOpen size={30} /> No documents here yet. Upload your first file above.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((d) => {
            const Icon = iconFor(d.name);
            return (
              <div key={d.id} data-testid={`doc-${d.id}`} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-[0_2px_10px_rgba(0,0,0,0.03)] flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-indigo-50 grid place-items-center shrink-0"><Icon size={20} className="text-indigo-600" /></div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 text-sm truncate" title={d.name}>{d.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{d.category}{d.size ? ` · ${kb(d.size)}` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-auto pt-1">
                  {d.editable && (
                    <button onClick={() => setEditing(d)} data-testid={`doc-edit-${d.id}`}
                      className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 flex items-center gap-1.5">
                      <Table2 size={13} /> Edit sheet
                    </button>
                  )}
                  <a href={`${BACKEND}${d.download_url}`} target="_blank" rel="noreferrer" data-testid={`doc-download-${d.id}`}
                    className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5">
                    <Download size={13} /> Open
                  </a>
                  <button onClick={() => del(d.id)} data-testid={`doc-delete-${d.id}`}
                    className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-1.5 ml-auto">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <SheetEditor doc={editing} companyId={companyId} onClose={() => setEditing(null)} />}
    </div>
  );
}

function SheetEditor({ doc, companyId, onClose }) {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/companies/${companyId}/documents/${doc.id}/sheet`)
      .then(({ data }) => setRows(data.rows?.length ? data.rows : [[""]]))
      .catch(() => { toast.error("Could not open sheet"); onClose(); });
  }, [doc.id]);

  const setCell = (r, c, v) => setRows((rs) => rs.map((row, ri) => (ri === r ? row.map((cell, ci) => (ci === c ? v : cell)) : row)));
  const addRow = () => setRows((rs) => [...rs, Array(rs[0]?.length || 1).fill("")]);
  const addCol = () => setRows((rs) => rs.map((row) => [...row, ""]));
  const delRow = (r) => setRows((rs) => (rs.length > 1 ? rs.filter((_, i) => i !== r) : rs));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}/documents/${doc.id}/sheet`, { rows });
      toast.success("Saved");
      onClose();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" data-testid="sheet-editor">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <div className="flex items-center gap-2 min-w-0">
            <FileSpreadsheet size={18} className="text-emerald-600 shrink-0" />
            <h3 className="font-heading font-bold text-slate-800 truncate">{doc.name}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={addRow} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 hover:bg-slate-50 flex items-center gap-1" data-testid="sheet-add-row"><Plus size={13} /> Row</button>
            <button onClick={addCol} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 hover:bg-slate-50 flex items-center gap-1" data-testid="sheet-add-col"><Plus size={13} /> Col</button>
            <button onClick={save} disabled={saving || !rows} data-testid="sheet-save"
              className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5 disabled:opacity-60">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
            </button>
            <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100" data-testid="sheet-close"><X size={16} /></button>
          </div>
        </div>
        <div className="overflow-auto p-3">
          {!rows ? (
            <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-emerald-600" size={26} /></div>
          ) : (
            <table className="border-collapse text-sm">
              <tbody>
                {rows.map((row, r) => (
                  <tr key={r} className="group">
                    <td className="sticky left-0 bg-slate-50 text-slate-400 text-xs px-2 border border-slate-200 text-center select-none">
                      <button onClick={() => delRow(r)} className="opacity-40 hover:opacity-100 hover:text-rose-600" title="Delete row">{r + 1}</button>
                    </td>
                    {row.map((cell, c) => (
                      <td key={c} className="border border-slate-200 p-0">
                        <input value={cell} onChange={(e) => setCell(r, c, e.target.value)} data-testid={`cell-${r}-${c}`}
                          className="w-40 px-2 py-1.5 outline-none focus:bg-emerald-50 focus:ring-1 focus:ring-emerald-400" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
